<script setup lang="ts">
import { useExplorerStore } from "../store";
import FilterChip from "@/modules/explorer/components/FilterBar/FilterChip.vue";
import PinChip from "@/modules/explorer/components/FilterBar/PinChip.vue";
import { computed, toRefs } from "vue";
import { ActivePin } from "@/modules/explorer/explorerTypes";
import FilterDialogHost from "@/modules/explorer/components/FilterDialog/FilterDialogHost.vue";
import { useFilterDialog } from "@/modules/explorer/components/FilterDialog/useFilterDialog";
import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import { useDashboardStore } from "@/modules/dashboards/store";

const { openFilterDialog } = useFilterDialog();

const props = defineProps<{
	surface: "explorer" | "dashboard";
	visiblePins: ActivePin[];
	activeFilters: ActiveFilterDTO[];
}>();

const { visiblePins, activeFilters, surface } = toRefs(props);

const isExplorer = computed(() => surface.value === "explorer");

const clearFilters = async () => {
	if (isExplorer.value) {
		await useExplorerStore().RESET_FILTERS();
	} else {
		useDashboardStore().clearFilters();
	}
};

const hasFilters = computed(() => {
	if (!isExplorer.value) {
		return (
			useDashboardStore().dashboardState.dashboard.active_filters.some(
				(filter) => !filter.isDefault
			) || false
		);
	}

	return (
		useExplorerStore().document?.active_filters?.some(
			(filter) => !filter.isDefault
		) || false
	);
});

const usedAsPin = (activeFilter: ActiveFilterDTO) => {
	return useExplorerStore().explorerState.activePins.some(
		(pin) => pin.dimension.name_clean === activeFilter.dimension.name_clean
	);
};
</script>

<template>
	<filter-dialog-host :surface="surface" />

	<div class="filter-bar">
		<span class="text-caption font-weight-bold">Filters:</span>

		<div class="scroll-chips d-flex ga-2">
			<template v-for="(activeFilter, index) in activeFilters" :key="index">
				<div v-if="usedAsPin(activeFilter) || !activeFilter.isDefault">
					<filter-chip
						:surface="surface"
						:active-filter="activeFilter"
						:index="index"
						:key="activeFilter.dimension.id"
						style="z-index: 100"
					/>
				</div>
			</template>
			<pin-chip
				v-for="(activePin, index) in visiblePins"
				:active-pin="activePin"
				:key="index"
				:surface="surface"
				style="z-index: 100"
			/>
		</div>

		<div class="d-flex ga-2">
			<v-btn
				color="error"
				variant="text"
				height="26"
				size="small"
				@click="clearFilters"
				v-if="hasFilters"
			>
				Clear
			</v-btn>
			<v-btn
				prepend-icon="fa: fa-regular fa-filter"
				variant="flat"
				height="26"
				size="small"
				@click="openFilterDialog"
			>
				New filter
			</v-btn>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.filter-bar {
	display: grid;
	grid-template-columns: auto 1fr auto;
	gap: 0.5rem;
	padding: 0.25rem 0;
	align-items: center;
	margin-top: auto;
}

.scroll-chips {
	overflow-y: hidden;
	scrollbar-width: thin;
	scrollbar-color: #dfe0e2 transparent;
}
</style>
